'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import { Image } from '@/components/raven/Image';
import { Expand } from '@themaven-net/raven-assets';
import { Button } from '@/components/raven/ui/Button';
import { useOverlayContext } from '@/context/OverlayContext';
import { GalleryCaption } from '@/components/raven/GalleryCaption';
import type { InlineGallerySlideProps } from '@/types/inline-gallery';
import { GalleryAttribution } from '@/components/raven/GalleryAttribution';

import styles from '../styles.module.css';

export const InlineGallerySlideComponent: FC<InlineGallerySlideProps> = ({
	alignment,
	description,
	galleryId,
	image,
	setSlideIdx,
	slideIdx,
	title,
}) => {
	const { openVariant } = useOverlayContext();

	if (!image) {
		return null;
	}

	const {
		alt,
		attribution,
		attribution_url: attributionUrl,
		caption,
		height,
		url,
		width,
	} = image;
	let alignmentClass;

	if (alignment) {
		alignmentClass = height > width ? styles['ar-9-16'] : styles['ar-16-9'];
	}

	const openModal = () => {
		setSlideIdx(slideIdx);
		openVariant(galleryId);

		// Prevents scrolling of content behind modal when modal is open.
		document.body.classList.add('no-scroll');
	};

	const alignFull = {
		[styles.alignFull]: alignment === 'align-full',
	};

	return (
		<div className={clsx(styles.slide, alignFull)}>
			{(title || description) && (
				<div className={clsx(styles.slideText, alignFull)}>
					{title && <h3 className={styles.slideTitle}>{title}</h3>}
					{description && (
						<p className={styles.slideDescription}>{description}</p>
					)}
				</div>
			)}
			<div className={styles.slideImageWrapper}>
				<Image
					alt={alt ?? ''}
					className={clsx(styles.slideImage, alignmentClass)}
					height={Number(height ?? 0)}
					src={url}
					width={Number(width ?? 0)}
				/>
				<Button
					className={clsx(styles.button, styles.expand)}
					onClick={openModal}
					variation="icon-large"
				>
					<Expand />
				</Button>
			</div>
			<GalleryCaption caption={caption} className={styles.caption} />
			<GalleryAttribution
				className={styles.attribution}
				sourceName={attribution}
				sourceUrl={attributionUrl}
			/>
		</div>
	);
};
